import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
	lazy,
	Suspense,
} from "react";
import toast from "react-hot-toast";
import { supabase } from "services/supabaseClient";

const IserbaAuthStep = ({ handlePlayerChosen }) => {
	const [search, setSearch] = useState("");
	const [players, setPlayers] = useState([]);
	const [selectedIserbaPlayer, setSelectedIserbaPlayer] = useState(null);
	const [selectedPlayer, setSelectedPlayer] = useState(null);
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const debounceRef = useRef(null);

	const trimmedSearch = useMemo(() => search.trim(), [search]);
	const terms = useMemo(() => trimmedSearch.split(/\s+/), [trimmedSearch]);

	const fetchPlayers = useCallback(async () => {
		if (trimmedSearch.length < 3) {
			setPlayers([]);
			return;
		}

		setLoading(true);

		let query = supabase
			.from("iserba_players")
			.select("*, players (id, quiz_id, avatar, pseudo, email)")
			.limit(5);

		if (terms.length === 2) {
			const [first, last] = terms;
			query = query.or(
				`and(first_name.ilike.%${first}%, last_name.ilike.%${last}%), and(last_name.ilike.%${first}%, first_name.ilike.%${last}%)`
			);
		} else {
			query = query.or(
				`first_name.ilike.%${trimmedSearch}%, last_name.ilike.%${trimmedSearch}%`
			);
		}

		const { data, error } = await query;

		if (error) {
			console.error("Error fetching players:", error);
			toast.error("Erreur lors de la recherche.");
		} else {
			setPlayers(data);
		}

		setLoading(false);
	}, [trimmedSearch, terms]);

	useEffect(() => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(fetchPlayers, 300);
		return () => clearTimeout(debounceRef.current);
	}, [fetchPlayers]);

	const handleSelectPlayer = useCallback(
		async (player) => {
			setSelectedIserbaPlayer(player);
			setSearch(`${player.first_name} ${player.last_name}`);
			setPlayers([]);

			if (!player.player_id) {
				toast.error("Aucun joueur associé trouvé.");
				return;
			}

			setLoading(true);

			const { data, error } = await supabase
				.from("players")
				.select("*")
				.eq("id", player.player_id)
				.single();

			setLoading(false);

			if (error) {
				console.error("Error fetching player details:", error);
				toast.error("Erreur lors de la récupération des détails du joueur.");
			} else {
				setSelectedPlayer(data);
			}
		},
		[handlePlayerChosen]
	);

	const handleSubmit = async () => {
		if (password === selectedIserbaPlayer.password) {
			handlePlayerChosen(selectedPlayer);
		} else {
			toast.error("Mot de passe incorrect.");
		}
	};

	return (
		<div className="flex flex-col items-center w-full h-full min-w-80 max-w-80 sm:min-w-96 sm:max-w-96 bg-white rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] relative">
			<div className="flex flex-col items-center w-full h-full p-6 gap-4">
				<h1 className="text-2xl text-gray-600 font-bold text-center mb-4">
					Recherchez votre profil en entrant votre prénom et votre nom.
				</h1>

				<div className="relative w-full ">
					<input
						type="text"
						placeholder="Tapez votre prénom et nom..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="border border-gray-300 rounded-full px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-black"
					/>

					{loading && (
						<p className="absolute text-center text-gray-200">Chargement...</p>
					)}

					{players.length > 0 && (
						<div className="absolute top-[-5px] border border-gray-300 rounded bg-white shadow-md mt-12 w-full max-h-60 overflow-auto z-10">
							{players.map((player) => (
								<div
									key={player.id}
									className="p-2 hover:bg-gray-100 cursor-pointer"
									onClick={() => handleSelectPlayer(player)}
								>
									{player.first_name} {player.last_name} - {player.company}
								</div>
							))}
						</div>
					)}
				</div>
				<input
					type="text"
					placeholder="Tapez votre mot de passe..."
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					className="border border-gray-300 rounded-full px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-black"
				/>
				<button
					onClick={handleSubmit}
					className="mt-8 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
				>
					Valider
				</button>
			</div>
		</div>
	);
};

export default IserbaAuthStep;
